import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { useDeleteMultipleEventosMutation } from '../../services/hooks/useEventos';
import { useInstructoresQuery } from '../../services/hooks/useInstructores';
import { useNivelesQuery } from '../../services/hooks/useNiveles';
import { useEventosByDateRangeQuery } from '../../services/hooks/useEventos';
import ConfirmationModal from '../common/ConfirmationModal';
import { formatDateToLocal } from '../../utils/utils';
import { useBranding } from '../../contexts/BrandingContext';

interface EliminarEventosMasivosProps {
    onClose: () => void;
    startDate: string;
    endDate: string;
}

const EliminarEventosMasivos: React.FC<EliminarEventosMasivosProps> = ({ onClose, startDate, endDate }) => {
    // Estados para las fechas
    const [fechaInicio, setFechaInicio] = useState<Date | null>(null);
    const [fechaFin, setFechaFin] = useState<Date | null>(null);
    
    // Estados para los filtros opcionales
    const [selectedInstructor, setSelectedInstructor] = useState<string>('');
    const [selectedNivel, setSelectedNivel] = useState<string>('');
    const [selectedSala, setSelectedSala] = useState<string>('');
    
    // Estados para el modal de confirmación
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState('');
    
    const { branding } = useBranding();
    const salasDisponibles = branding?.rooms ?? [];
    
    // Queries
    const { data: instructores, isLoading: loadingInstructores } = useInstructoresQuery();
    const { data: niveles, isLoading: loadingNiveles } = useNivelesQuery();
    
    // Convertir fechas usando la función utilitaria
    const fechaInicioStr = fechaInicio ? formatDateToLocal(fechaInicio) : '';
    const fechaFinStr = fechaFin ? formatDateToLocal(fechaFin) : '';
    
    // Query para verificar eventos (solo se ejecuta cuando hay fechas seleccionadas)
    const { data: eventosEnRango, isLoading: loadingEventos } = useEventosByDateRangeQuery(
        fechaInicioStr,
        fechaFinStr,
        !!fechaInicio && !!fechaFin
    );
    
    // Mutación
    const deleteMultipleEventosMutation = useDeleteMultipleEventosMutation(startDate, endDate);

    // Función para contar eventos - CORREGIDA para objetos populados
    const contarEventos = () => {
        if (!eventosEnRango) return { total: 0, conClientes: 0, sinClientes: 0 };
        
        // Filtrar eventos según los criterios seleccionados
        const eventosFiltrados = eventosEnRango.filter(evento => {
            // Filtro por instructor - buscar en el objeto populado
            if (selectedInstructor) {
                const eventoInstructorId = evento.instructor?._id || evento.instructor_id;
                if (eventoInstructorId !== selectedInstructor) {
                    return false;
                }
            }
            
            // Filtro por nivel - buscar en el objeto populado
            if (selectedNivel) {
                const eventoNivelId = evento.nivel?._id || evento.nivel_id;
                if (eventoNivelId !== selectedNivel) {
                    return false;
                }
            }
            
            // Filtro por sala - este sí está directo
            if (selectedSala && evento.sala !== selectedSala) {
                return false;
            }
            
            return true;
        });
        
        let eventosConClientes = 0;
        let eventosSinClientes = 0;
        
        eventosFiltrados.forEach(evento => {
            // clientesInscritos es un array según el JSON
            // D2: una clase a la que solo va alguien de prueba tampoco está vacía.
            // El backend cuenta igual, así que esta vista previa debe coincidir o
            // el recuento mentiría sobre lo que se va a borrar.
            const tieneClientes = (Array.isArray(evento.clientesInscritos) && evento.clientesInscritos.length > 0)
                || (Array.isArray(evento.enlistedProspects) && evento.enlistedProspects.length > 0);
                
            if (tieneClientes) {
                eventosConClientes++;
            } else {
                eventosSinClientes++;
            }
        });
        
        return { 
            total: eventosFiltrados.length, 
            conClientes: eventosConClientes, 
            sinClientes: eventosSinClientes 
        };
    };

    // Función para manejar la eliminación
    const handleEliminarEventos = async () => {
        if (!fechaInicio || !fechaFin) {
            toast.error('Por favor seleccione el rango de fechas');
            return;
        }

        const conteo = contarEventos();
        
        if (conteo.total === 0) {
            toast.error('No se encontraron eventos en el rango de fechas seleccionado');
            return;
        }

        // Preparar mensaje de confirmación
        let mensaje = `Se encontraron <strong>${conteo.total}</strong> eventos en el rango seleccionado.<br/>`;
        
        if (conteo.conClientes > 0) {
            mensaje += `<strong>${conteo.conClientes}</strong> eventos tienen clientes inscritos y NO se eliminarán.<br/>`;
        }
        
        if (conteo.sinClientes > 0) {
            mensaje += `<strong>${conteo.sinClientes}</strong> eventos SIN clientes serán eliminados.<br/><br/>`;
            mensaje += '¿Desea continuar con la eliminación?';
        } else {
            mensaje += '<br/>No hay eventos sin clientes para eliminar.';
            toast.warning('No hay eventos sin clientes para eliminar');
            return;
        }

        setConfirmMessage(mensaje);
        setShowConfirmModal(true);
    };

    // Función para confirmar eliminación
    const confirmarEliminacion = async () => {
        setShowConfirmModal(false);
        setIsProcessing(true);

        try {
            const params = {
                fechaInicio: formatDateToLocal(fechaInicio!),
                fechaFin: formatDateToLocal(fechaFin!),
                ...(selectedInstructor && { instructor_id: selectedInstructor }),
                ...(selectedNivel && { nivel_id: selectedNivel }),
                ...(selectedSala && { sala: selectedSala })
            };

            await deleteMultipleEventosMutation.mutateAsync(params);
            
            // Limpiar estados
            setFechaInicio(null);
            setFechaFin(null);
            setSelectedInstructor('');
            setSelectedNivel('');
            setSelectedSala('');
            
            onClose();
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <>
            <div className="modal-overlay fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="relative modal-content bg-white rounded-lg p-6 w-full max-w-4xl overflow-y-auto max-h-[80vh]">
                    <button 
                        onClick={onClose} 
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                    >
                        <X size={24} />
                    </button>
                    
                    <div className="flex flex-col justify-between items-left mb-4">
                        <h2 className="text-2xl font-bold text-red-600">Eliminar Eventos Masivos</h2>
                        <div className='p-2'>
                            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-3 mb-4">
                                <div className="flex items-center">
                                    <AlertTriangle size={20} className="mr-2" />
                                    <div>
                                        <p className="font-semibold">⚠️ Advertencia</p>
                                        <p className="text-sm">Solo se eliminarán eventos SIN clientes inscritos. Los eventos con clientes no serán afectados.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Selección de fechas */}
                    <div className='mb-4 p-4 border rounded'>
                        <h3 className="text-lg font-bold mb-2">1. Seleccione el rango de fechas</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Fecha Inicio:</label>
                                <DatePicker
                                    selected={fechaInicio}
                                    onChange={(date) => setFechaInicio(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    placeholderText="Seleccione fecha inicio"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Fecha Fin:</label>
                                <DatePicker
                                    selected={fechaFin}
                                    onChange={(date) => setFechaFin(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    placeholderText="Seleccione fecha fin"
                                    minDate={fechaInicio || undefined}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Filtros opcionales */}
                    {fechaInicio && fechaFin && (
                        <div className='mb-4 p-4 border rounded'>
                            <h3 className="text-lg font-bold mb-2">2. Filtros opcionales</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Dropdown para Instructores */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Instructor:</label>
                                    <select 
                                        value={selectedInstructor} 
                                        onChange={(e) => setSelectedInstructor(e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                        disabled={loadingInstructores}
                                    >
                                        <option value="">Todos los instructores</option>
                                        {instructores?.map(instructor => (
                                            <option key={instructor._id} value={instructor._id}>
                                                {instructor.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Dropdown para Niveles */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nivel:</label>
                                    <select 
                                        value={selectedNivel} 
                                        onChange={(e) => setSelectedNivel(e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                        disabled={loadingNiveles}
                                    >
                                        <option value="">Todos los niveles</option>
                                        {niveles?.map(nivel => (
                                            <option key={nivel._id} value={nivel._id}>
                                                {nivel.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Dropdown para Salas */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Sala:</label>
                                    <select 
                                        value={selectedSala}
                                        onChange={(e) => setSelectedSala(e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    >
                                        <option value="">Todas las salas</option>
                                        {salasDisponibles.map((room) => (
                                            <option key={room.name} value={room.name}>
                                                {room.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Información de eventos encontrados */}
                    {fechaInicio && fechaFin && (
                        <div className='mb-4 p-4 border rounded bg-blue-50'>
                            <h3 className="text-lg font-bold mb-2">3. Información del rango seleccionado</h3>
                            {loadingEventos ? (
                                <p>Cargando eventos...</p>
                            ) : (
                                <div>
                                    {(() => {
                                        const conteo = contarEventos();
                                        return (
                                            <div className="text-sm">
                                                <p><strong>Total de eventos:</strong> {conteo.total}</p>
                                                <p><strong>Eventos con clientes:</strong> {conteo.conClientes} (no se eliminarán)</p>
                                                <p><strong>Eventos sin clientes:</strong> {conteo.sinClientes} (se eliminarán)</p>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Botón de eliminar */}
                    {fechaInicio && fechaFin && !loadingEventos && (
                        <div className="flex justify-center mb-4">
                            <button 
                                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
                                onClick={handleEliminarEventos}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Eliminando...' : 'Eliminar Eventos'}
                            </button>
                        </div>
                    )}

                    <div className="flex justify-end">
                        <button className="mt-2 px-4 py-2 bg-gray-500 text-white rounded" onClick={onClose}>
                            Cerrar
                        </button>
                    </div>  
                </div>
            </div>

            {/* Modal de confirmación */}
            <ConfirmationModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={confirmarEliminacion}
                confirmMessage={confirmMessage}
            />
        </>
    );
};

export default EliminarEventosMasivos;